// Wire format between the authoritative simulation and the screens watching it.
// snapshot(): the moving parts, sent ~10x a second. mapPayload(): land and borders, sent on join and after annexing.
// buildView(): turns them back into a state object the same game functions can read, for drawing and the HUD.
import { G, contourLoops, computeDF, rebuildAdj, setDims } from './game.js';

export const PROTOCOL = 4;
const r1 = v => Math.round(v * 10) / 10;
const r3 = v => Math.round(v * 1000) / 1000;
// a shape on the move is fully described by who sent it and who it is for (its shape is always its sender's)
// a shape on the wire: sender and target packed into one number (up to 64 cities)
const code = u => u.sender * 64 + u.target;

export function snapshot() {
  return {
    v: PROTOCOL, time: r3(G.time), phase: G.phase, netV: G.netV, mapV: G.mapV, cheats: G.cheats, mods: G.mods, level: G.level, winner: G.winner, np: G.np,
    seats: G.seats.map(s => ({ name: s.name, human: s.human, claim: s.claim, connected: s.connected !== false })),
    players: G.players.map(p => ({
      id: p.id, shape: p.shape, color: p.color, name: p.name, isHuman: p.isHuman, connected: p.connected, afk: !!p.afk, region: p.region, alive: p.alive,
      coins: Math.floor(p.coins * 100) / 100, spare: p.spare, surgeAt: p.surgeAt, lineLimit: p.lineLimit, rate: p.rate, rateMax: p.rateMax, target: p.target, lastTarget: p.lastTarget,
      prod: r3(p.prod), depots: p.depots, bank: p.bank, banking: p.banking, stats: p.stats, st: p.st,
      lines: p.lines.map(L => ({ id: L.id, color: L.color, st: L.st, loop: L.loop, trains: L.trains.map(T => [T.idx, T.to, r3(T.t), T.dir, r3(T.wait), T.cap, T.units.map(code)]) })),
    })),
    regions: G.regions.map(r => [r.owner, r.dead ? 1 : 0, r.mergedInto ?? -1]),
    stations: [...G.stations.values()].map(s => [s.id, s.owner, s.kind, r1(s.x), r1(s.y), s.shape || 0, s.to ?? -1, s.pid ?? -1, r3(s.ang || 0),
      r3(s.crowd), (s.warehouse ? 1 : 0) | (s.customs ? 2 : 0), r3(s.graceUntil || 0), r3(s.born), s.units.map(code)]),
    flights: G.flights.map(f => [r1(f.x0), r1(f.y0), r1(f.x1), r1(f.y1), r3(f.t), r3(f.dur), code(f.u)]),
  };
}

// run-length encode the territory grid: [value, count, value, count, ...]
export function rle(a) { const out = []; for (let i = 0; i < a.length;) { let j = i; while (j < a.length && a[j] === a[i]) j++; out.push(a[i], j - i); i = j; } return out; }
export function unrle(r, n) { const a = new Int16Array(n); let k = 0; for (let i = 0; i < r.length; i += 2) { a.fill(r[i], k, k + r[i + 1]); k += r[i + 1]; } return a; }

export function mapPayload() {
  return {
    mapV: G.mapV, n: G.map.own.length, own: rle(G.map.own), bounds: G.map.bounds, w: G.map.w, h: G.map.h, name: G.map.name, form: G.map.form,
    regions: G.regions.map(r => ({ id: r.id, c: r.c, hearts: r.hearts, nb: r.nb.map(n => ({ to: n.to, gate: n.gate, pid: n.pid })) })),
  };
}

// rebuild land, outlines and distance-to-border on the receiving side from the grid alone
export function buildMap(payload) {
  const map = { own: unrle(payload.own, payload.n), bounds: payload.bounds, w: payload.w || 1600, h: payload.h || 1000, name: payload.name, form: payload.form }, own = map.own;
  setDims(map.w, map.h);   // the outlines below are traced on this map's grid
  computeDF(map);
  const regions = payload.regions.map(r => ({ ...r, loops: contourLoops(k => own[k] === r.id), owner: null, dead: false, mergedInto: null }));
  map.landLoops = contourLoops(k => own[k] >= 0);
  map.regions = regions;   // online map art reads map.regions, as solo's genMap map does
  return { map, regions, mapV: payload.mapV };
}

export function buildView(snap, mapData, prev) {
  const sameNet = prev && prev.netV === snap.netV && prev.mapV === snap.mapV;
  const V = {
    phase: snap.phase, time: snap.time, netV: snap.netV, mapV: snap.mapV, geoV: sameNet ? prev.geoV : 0, cheats: snap.cheats, mods: snap.mods || [], level: snap.level || 'normal', winner: snap.winner,
    np: snap.np, seats: snap.seats, map: mapData.map, regions: mapData.regions, stations: new Map(), players: [], flights: [], events: [], rd: {}, xq: {},
  };
  snap.regions.forEach(([owner, dead, merged], i) => { const r = V.regions[i]; if (r) { r.owner = owner; r.dead = !!dead; r.mergedInto = merged < 0 ? null : merged; } });
  const shapeOf = snap.players.map(p => p.shape);
  const decode = c => ({ shape: shapeOf[c >> 6], sender: c >> 6, target: c & 63 });
  for (const s of snap.stations) {
    const [id, owner, kind, x, y, shape, to, pid, ang, crowd, flags, graceUntil, born, units] = s;
    V.stations.set(id, { id, owner, kind, x, y, shape: shape || undefined, to: to < 0 ? undefined : to, pid: pid < 0 ? undefined : pid, ang, crowd,
      warehouse: !!(flags & 1), customs: !!(flags & 2), graceUntil, born, units: units.map(decode) });
  }
  for (const sp of snap.players) {
    const old = sameNet && prev.players[sp.id];
    const p = { ...sp, adj: old ? old.adj : new Map(), dist: old ? old.dist : new Map(), needsAdj: !old };
    p.lines = sp.lines.map(L => {
      const oldL = old && old.lines.find(o => o.id === L.id);
      return { id: L.id, owner: sp.id, color: L.color, st: L.st, loop: L.loop, geo: oldL ? oldL.geo : undefined,
        trains: L.trains.map(([idx, to, t, dir, wait, cap, units], i) => {
          // vt is where the screen shows the train; it carries over while the train stays on the same stretch
          const o = oldL && oldL.trains[i], same = o && o.idx === idx && o.to === to && to != null;
          return { idx, to, t, vt: same ? o.vt ?? o.t : t, dir, wait, cap, units: units.map(decode) };
        }) };
    });
    V.players.push(p);
  }
  V.flights = snap.flights.map(([x0, y0, x1, y1, t, dur, c]) => ({ x0, y0, x1, y1, t, dur, u: decode(c) }));
  return V;
}
// after setState(view): station graphs for any player whose network changed
export function finishView(V) { for (const p of V.players) if (p.needsAdj) { rebuildAdj(p); p.needsAdj = false; } }
